import { z } from 'zod';
import { hrWorkflowCommandSchema, hrWorkflowViewSchema, pdfTemplateVersionSchema, type HrWorkflowCommand, type HrWorkflowView, type PdfTemplateVersion } from '@vigeo/shared';
import { fixtureMode } from '../../lib/data-mode.js';

/** The id every fixture offer carries, so a screen can be opened at it without a round trip. */
export const FIXTURE_OFFER_ID = 'hrw:fixture-offer';

/**
 * FIXTURES REACH THE APP ONLY THROUGH THIS GUARDED IMPORT, and `fixtureMode` is the literal
 * `false` in a build, so the branch is dead code and `dev-fixtures/` never enters the bundle —
 * the shape `scripts/check-production-bundle.mjs` requires, and the same one
 * `features/files/api.ts` uses.
 *
 * The fixture offer used to be BUILT HERE, which is why rows 2.1 and 2.2 were unreachable: an
 * inline record could reach `reviewed` but both rows are gated on an offer STEP, and inventing
 * step-and-signature fixtures in `apps/web/src` would also have put fictional data where
 * `check:file-stores` forbids naming a file category. It lives in `dev-fixtures/hr-workflow.ts`
 * now, beside the rest of the fixture set (#334).
 */
async function fixtures() {
  return import('../../../dev-fixtures/hr-workflow.js');
}

export async function fetchHrWorkflow(personId: string): Promise<HrWorkflowView> {
  if (fixtureMode) return (await fixtures()).fixtureWorkflow(personId);
  return read(await fetch(`/api/people/${encodeURIComponent(personId)}/hr-workflow`));
}
export async function performHrWorkflow(personId: string, command: HrWorkflowCommand): Promise<HrWorkflowView> {
  if (fixtureMode) throw new Error('Workflow changes require the local test API; design fixtures are read-only.');
  return read(await fetch(`/api/people/${encodeURIComponent(personId)}/hr-workflow`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(hrWorkflowCommandSchema.parse(command)),
  }));
}
async function read(response: Response): Promise<HrWorkflowView> {
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string' ? body.error : 'The workflow could not be loaded.';
    throw new Error(error);
  }
  return hrWorkflowViewSchema.parse(body);
}

/**
 * The offer letter as the portal will serve it, for row 1.3's preview (#261).
 *
 * Two calls, mirroring the portal's own split: the pinned template shape and step config as JSON,
 * then the bytes. Neither is stored anywhere — the server rebuilds the same artifact the issue
 * path would file (see `hrWorkflowOfferPreviewHandler`).
 */
export interface OfferPreview {
  readonly version: PdfTemplateVersion;
  readonly config: unknown;
}

export async function fetchOfferPreview(personId: string, recordId: string): Promise<OfferPreview> {
  const base = `/api/people/${encodeURIComponent(personId)}/hr-workflow/${encodeURIComponent(recordId)}/offer-preview`;
  const response = await fetch(base);
  const body: unknown = await response.json();
  if (!response.ok) {
    // The server's own sentence, so the reviewer is told what is wrong rather than shown an empty
    // frame (#261). Never a status code dressed up as an explanation.
    throw new Error(typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string'
      ? body.error : 'This offer letter could not be prepared for review.');
  }
  const parsed = z.object({ version: pdfTemplateVersionSchema, config: z.unknown() }).parse(body);
  return { version: parsed.version, config: parsed.config };
}

export async function fetchOfferPreviewBytes(personId: string, recordId: string): Promise<Uint8Array> {
  const response = await fetch(`/api/people/${encodeURIComponent(personId)}/hr-workflow/${encodeURIComponent(recordId)}/offer-preview.pdf`);
  if (!response.ok) throw new Error('This offer letter could not be prepared for review.');
  return new Uint8Array(await response.arrayBuffer());
}
